import { createContext, useContext } from "react";
import type {
    ChatModelRunResult,
    ChatModelRunOptions,
} from "@assistant-ui/react";

import type { AttachTarget } from "../adapters/chat-model-adapter";

/**
 * One-shot signal carrying the turn to watch from the recovering affordance
 * (the producer) to the `chat-model-adapter` (the consumer), on the same
 * pattern as `RetryContext`. The adapter is built once per `RuntimeProvider`
 * and serves every thread, so the target cannot be a construction-time dep:
 * a statically supplied one sends every composer turn down the attach branch.
 *
 * `attachStream` is that same singleton's runner, which `resumeRun` needs as
 * its `stream`. It is narrowed here because `resumeRun` accepts only an
 * AsyncGenerator while `ChatModelAdapter["run"]` also permits a
 * Promise-returning form.
 */
export interface AttachContextValue {
    armAttachTarget: (target: AttachTarget) => void;
    attachStream: (
        options: ChatModelRunOptions
    ) => AsyncGenerator<ChatModelRunResult, void, unknown>;
}

export const AttachContext = createContext<AttachContextValue | undefined>(
    undefined
);

/** Undefined outside a RuntimeProvider, so callers can degrade rather than throw. */
export function useAttachRuntime(): AttachContextValue | undefined {
    return useContext(AttachContext);
}
